<template>
  <div v-if="show" class="candidate-questionnaire-container" :class="[displayMode, hasResumeView && !onlyOpenQuestion && 'has-resume-view', qrImage && 'qrImage-container']">
    <template v-if="qrImage">
      <el-card class="qrcode-card">
        <div class="qrcode-container">
          <img class="qrcode" :src="qrImage">
        </div>
      </el-card>
      <div class="qrcode-tip">{{ $t(`${i18nPrefix}.label.elcQuestionQRCodeTip`) }}</div>
    </template>
    <template v-else>
      <div v-if="isTreeMode && !isCandidateAnswerMode" class="tree-summary">
        <el-progress :percentage="treeProgress.percent" :stroke-width="6" :show-text="false"></el-progress>
        <div v-if="friendlyTreeAnswerPath.length > 0" class="tree-path">
          <div v-for="node in friendlyTreeAnswerPath" :key="node.id" class="path-node">
            <div class="path-question-line">
              <span class="path-depth">{{ node.depthLabel }}</span>
              <span class="path-title">{{ node.title }}</span>
            </div>
            <div v-if="getPathAnswerText(node)" class="path-answer-line">
              <span class="path-answer-label">{{ getPathAnswerLabel(node) }}</span>
              <span class="path-answer">{{ getPathAnswerText(node) }}</span>
            </div>
          </div>
        </div>
      </div>
      <template v-if="isCandidateAnswerMode">
        <QuestionAnswerTree
          :questions="presentationStepData"
          :allQuestions="allQuestionList"
          :displayMode="displayMode"
          :errorMessageFormatter="getQuestionErrorMessage"
          @answer-change="handleQuestionAnswerChange"
          @file-upload="handleFileUpload"
          @register-question="registerQuestionControl"
          @unregister-question="unregisterQuestionControl"
        />
      </template>
      <div
        v-for="(question) in isCandidateAnswerMode ? [] : stepData"
        :key="question.id"
        class="tree-question-item"
        :class="{
          'is-tree-mode': isTreeMode,
          'is-tree-child': isTreeMode && question.treeMeta.depth > 0,
          'is-tree-terminal': isTreeMode && question.treeMeta.isTerminal
        }"
        :style="getQuestionTreeStyle(question)"
      >
        <Question
          v-model="question.answer"
          :ref="`q_${question.id}`"
          :type="question.type"
          :title="question.title"
          :surveyType="question.surveyType"
          :isRequired="question.isRequired"
          :optionList="question.optionList"
          :uploaderNeedFileDescription="question.uploaderNeedFileDescription"
          :uploaderFileDescription.sync="question.uploaderFileDescription"
          :uploaderFileTypeList="question.uploaderFileTypeList"
          :uploaderFileSize="question.uploaderFileSize"
          :uploaderTipList="question.uploaderTipList"
          :policyLink="question.policyLink"
          :errorMessage="getQuestionErrorMessage(question.errorMessage)"
          @change="handleQuestionAnswerChange(question, $event)"
          @file-upload="handleFileUpload(question, ...arguments)"
        />
      </div>
      <RectLoading v-show="loading" class="loading"></RectLoading>
    </template>
  </div>
</template>

<script>
import _ from 'lodash'
import mixin from './mixin'
import Question, { QUESTION_TYPE } from './question.vue'
import { QUESTION_TYPE as OPEN_QUESTION_TYPE } from '@/constant/openQuestion'
import { ONBOARDING_QUESTION_TYPE } from '@/constant/common'
import { QUESTIONNAIRE_QUESTION_TYPE } from '@/constant/questionnaire'
import { addI18nProperty } from '@/i18n'
import { uploadFile, getFileUID } from '@/utils/qiniu'
import { mapActions, mapGetters } from 'vuex'
import RectLoading from '@/components/loading/rectLoading.vue'
import QuestionAnswerTree from './questionAnswerTree.vue'
import moment from 'moment'
import {
  OPEN_QUESTION_TREE_MODE,
  clearChildTreeAnswers,
  detectTreeMode,
  enhanceTreeQuestion,
  getTreeAnswerPath,
  getTreeKnockoutState,
  getTreeProgress,
  getVisibleTreeQuestions
} from '@/utils/openQuestionTree'

export const QUESTIONNAIRE_TYPE = {
  OPEN_QUESTION: 'OPEN_QUESTION',
  ONBOARDING: 'ONBOARDING'
}

const OPEN_QUESTION_TYPE_MAP = {
  [OPEN_QUESTION_TYPE.VIDEO]: QUESTION_TYPE.VIDEO,
  [OPEN_QUESTION_TYPE.ATTACHMENT]: QUESTION_TYPE.ATTACHMENT,
  [OPEN_QUESTION_TYPE.SELECT]: QUESTION_TYPE.RADIO,
  [OPEN_QUESTION_TYPE.CHECKBOX]: QUESTION_TYPE.CHECKBOX,
  [OPEN_QUESTION_TYPE.TEXT]: QUESTION_TYPE.TEXTAREA,
  [OPEN_QUESTION_TYPE.SINGLE_SELECT]: QUESTION_TYPE.SELECT,
  [OPEN_QUESTION_TYPE.MULTIPLE_SELECT]: QUESTION_TYPE.MULTIPLE,
  [OPEN_QUESTION_TYPE.DATE]: QUESTION_TYPE.DATE
}

const ONBOARDING_QUESTION_TYPE_MAP = {
  [QUESTION_TYPE.TEXT]: [
    ONBOARDING_QUESTION_TYPE.TEXT.value,
    QUESTIONNAIRE_QUESTION_TYPE.DATE_RANGE.value,
    QUESTIONNAIRE_QUESTION_TYPE.REPEAT_GROUP.value
  ],
  [QUESTION_TYPE.SELECT]: [
    ONBOARDING_QUESTION_TYPE.SINGLE.value,
    ONBOARDING_QUESTION_TYPE.LOCATION.value,
    ONBOARDING_QUESTION_TYPE.NATIONALITY.value,
    ONBOARDING_QUESTION_TYPE.COUNTRY.value,
    ONBOARDING_QUESTION_TYPE.PROVINCE.value,
    ONBOARDING_QUESTION_TYPE.CITY.value,
    ONBOARDING_QUESTION_TYPE.LANGUAGE.value
  ],
  [QUESTION_TYPE.DATE]: [ONBOARDING_QUESTION_TYPE.DATE.value],
  [QUESTION_TYPE.ATTACHMENT]: [ONBOARDING_QUESTION_TYPE.ATTACHMENT.value],
  [QUESTION_TYPE.POLICY]: [ONBOARDING_QUESTION_TYPE.LINK.value]
}

const MOBILE_OPEN_QUESTION_PATH = 'candidate-job-survey/'
const MOBILE_INVITE_QUESTION_PATH = 'candidate-invite-job-survey/'

export default {
  mixins: [mixin],
  components: {
    Question,
    QuestionAnswerTree,
    RectLoading
  },
  props: {
    infoPropName: {
      type: String,
      required: true
    },
    metaPropName: {
      type: String,
      default: null
    },
    showQrImagePropName: {
      type: String,
      default: null
    },
    questionnaireType: {
      type: String,
      default: QUESTIONNAIRE_TYPE.OPEN_QUESTION
    },
    hasResumeView: {
      type: Boolean,
      default: false
    },
    jobId: {
      type: String,
      default: null
    },
    onlyOpenQuestion: {
      type: Boolean,
      default: false
    },
    setting: {
      type: Object,
      default: () => ({})
    },
    displayMode: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      i18nPrefix: 'admin.application.candidate',
      hasForm: false,
      stepData: {},
      allQuestionList: [],
      mode: OPEN_QUESTION_TREE_MODE.FLAT,
      qrImage: null,
      account: null,
      questionControlRefs: {}
    }
  },
  computed: {
    questionnaireMeta () {
      return this.metaPropName ? (_.get(this.candidateInfo, this.metaPropName) || {}) : {}
    },
    isCandidate () {
      return Boolean(this.setting?.isCandidate)
    },
    isTreeMode () {
      return this.mode === OPEN_QUESTION_TREE_MODE.TREE
    },
    isCandidateAnswerMode () {
      return this.displayMode === 'candidate-answer' && this.questionnaireType === QUESTIONNAIRE_TYPE.OPEN_QUESTION
    },
    presentationStepData () {
      return Array.isArray(this.stepData) ? this.stepData : []
    },
    treeAnswerPath () {
      return this.isTreeMode ? getTreeAnswerPath(this.allQuestionList) : []
    },
    friendlyTreeAnswerPath () {
      if (!this.isTreeMode) {
        return []
      }
      return _.filter(this.treeAnswerPath, node => this.isLinkedTreePathQuestion(node.id))
    },
    treeProgress () {
      return this.isTreeMode ? getTreeProgress(this.allQuestionList) : { current: 0, total: 0, percent: 0 }
    },
    treeKnockout () {
      return this.isTreeMode ? getTreeKnockoutState(this.allQuestionList) : false
    }
  },
  created () {
    this.account = this.getCurrentAccount()
  },
  methods: {
    ...mapGetters('account', ['getCurrentAccount']),
    ...mapActions('application', ['handleOpenQuestionUploadedVideo', 'getOpenQuestionUploadedVideoDuration']),
    ...mapActions('questionTree', ['hydrateTreeState']),
    formatOpenQuestion (questionList) {
      const vm = this
      return _.map(questionList, (question, index) => {
        const openQuestionType = question.type
        const questionType = OPEN_QUESTION_TYPE_MAP[openQuestionType]
        const rawOptions = question.options || question.option || []
        let answer = question.answer_value
        let uploaderFileTypeList = null
        const uploaderFileSize = 200 * 1024 * 1024
        let uploaderFileDescription = null
        if (openQuestionType === OPEN_QUESTION_TYPE.SELECT || openQuestionType === OPEN_QUESTION_TYPE.SINGLE_SELECT) {
          answer = answer && parseInt(answer)
        } else if (openQuestionType === OPEN_QUESTION_TYPE.CHECKBOX || openQuestionType === OPEN_QUESTION_TYPE.MULTIPLE_SELECT) {
          answer = _.chain(answer).split(',').map(item => parseInt(item)).filter().value()
        } else if (openQuestionType === OPEN_QUESTION_TYPE.VIDEO) {
          uploaderFileTypeList = ['video/mp4', 'video/x-msvideo', 'video/ogg', 'video/quicktime']
          uploaderFileDescription = question.answer_value
          answer = question.answer_file_key
        } else if (openQuestionType === OPEN_QUESTION_TYPE.ATTACHMENT) {
          uploaderFileDescription = question.answer_value
          answer = question.answer_file_key
        }
        const questionInfo = {
          _question: question,
          id: question.id,
          depth: question.depth,
          tree_path: question.tree_path,
          is_terminal: question.is_terminal,
          parent_question_id: question.parent_question_id,
          parent_option_id: question.parent_option_id,
          option: rawOptions,
          order: question.order || index,
          type: questionType,
          title: question.title,
          isRequired: question.required,
          answer,
          surveyType: question.survey_type,
          optionList: _.map(rawOptions, option => {
            return {
              label: option.description,
              value: option.id
            }
          }),
          uploaderFileTypeList,
          uploaderFileSize,
          uploaderNeedFileDescription: [OPEN_QUESTION_TYPE.VIDEO, OPEN_QUESTION_TYPE.ATTACHMENT].includes(openQuestionType),
          uploaderFileDescription,
          errorMessage: null,
          _treeAnswerSnapshot: _.cloneDeep(answer)
        }
        if (openQuestionType === OPEN_QUESTION_TYPE.VIDEO) {
          Object.defineProperty(questionInfo, 'uploaderTipList', {
            configurable: true,
            enumerable: true,
            get () {
              return [
                vm.isCandidate ? vm.$t(`${vm.i18nPrefix}.message.uploadTip.videoUploadTipsPC`) : '',
                vm.$t(`${vm.i18nPrefix}.message.uploadTip.openQuestionVideo`, { duration: question.duration })
              ]
            }
          })
        } else if (openQuestionType === OPEN_QUESTION_TYPE.ATTACHMENT) {
          Object.defineProperty(questionInfo, 'uploaderTipList', {
            configurable: true,
            enumerable: true,
            get () {
              return [
                vm.$t(`${vm.i18nPrefix}.message.uploadTip.attachment`, { size: 200 })
              ]
            }
          })
        }
        return enhanceTreeQuestion(questionInfo)
      })
    },
    syncQuestionTree () {
      if (!this.isTreeMode) {
        this.stepData = this.allQuestionList
        return
      }
      this.stepData = getVisibleTreeQuestions(this.allQuestionList)
      this.hydrateTreeState({ questions: this.allQuestionList, mode: this.mode })
    },
    getQuestionTreeStyle (question) {
      if (!this.isTreeMode) {
        return null
      }
      const depth = _.get(question, 'treeMeta.depth', 0)
      return {
        '--tree-indent': `${Math.min(depth, 6) * 34}px`
      }
    },
    getQuestionParentRelation (question) {
      if (!this.isTreeMode) {
        return null
      }
      const questionId = question.id
      let matchedOption = null
      const parentQuestion = _.find(this.allQuestionList, parentQuestion => {
        const optionTreeMap = _.get(parentQuestion, 'treeMeta.optionTreeMap', [])
        matchedOption = _.find(optionTreeMap, item => item.nextQuestionId === questionId)
        return Boolean(matchedOption)
      })
      if (parentQuestion && matchedOption) {
        return {
          parentTitle: parentQuestion.title,
          optionLabel: matchedOption.label
        }
      }
      return null
    },
    isLinkedTreePathQuestion (questionId) {
      const question = _.find(this.allQuestionList, { id: questionId })
      if (!question) {
        return false
      }
      const depth = _.get(question, 'treeMeta.depth', 0)
      const hasParent = Boolean(this.getQuestionParentRelation(question))
      const hasChildOption = _.some(_.get(question, 'treeMeta.optionTreeMap', []), option => option.hasNextQuestion)
      return depth > 0 || hasParent || hasChildOption
    },
    getPathAnswerText (node) {
      return _.isEmpty(node.selectedOptions) ? (node.answerLabel || '') : _.map(node.selectedOptions, 'label').join(', ')
    },
    getPathAnswerLabel (node) {
      return this.$t(`${this.i18nPrefix}.label.tree.${!_.isEmpty(node.selectedOptions) ? 'selected' : 'answered'}`)
    },
    resetClearedTreeAnswerSnapshots (questionIds) {
      if (_.isEmpty(questionIds)) {
        return
      }
      const questionMap = _.keyBy(this.allQuestionList, 'id')
      _.forEach(questionIds, questionId => {
        const question = questionMap[questionId]
        if (question) {
          question._treeAnswerSnapshot = _.cloneDeep(question.answer)
        }
      })
    },
    registerQuestionControl (questionId, control) {
      this.$set(this.questionControlRefs, String(questionId), control)
    },
    unregisterQuestionControl (questionId, control) {
      const key = String(questionId)
      if (this.questionControlRefs[key] === control) {
        this.$delete(this.questionControlRefs, key)
      }
    },
    formatOnboardingQuestion (questionList) {
      const vm = this
      const questionPidMap = {}
      const questionMap = {}
      const questionInfoList = _.map(questionList, question => {
        const onboardingQuestionType = question.type
        const questionType = _.findKey(ONBOARDING_QUESTION_TYPE_MAP, typeList => typeList.includes(onboardingQuestionType))
        const uploaderFileSize = 200 * 1024 * 1024
        const questionInfo = {
          _question: question,
          _parent: null,
          _parentAnswer: null,
          id: question.id,
          type: questionType,
          isRequired: question.is_required,
          answer: question.answer,
          optionList: _.map(question.options, option => addI18nProperty({
            id: option.id,
            pid: option.pid,
            value: option.id
          }, 'label', 'content.cn', 'content.en', option)),
          uploaderFileSize,
          errorMessage: null
        }
        addI18nProperty(questionInfo, 'title', 'title.cn', 'title.en', question)
        if (onboardingQuestionType === ONBOARDING_QUESTION_TYPE.LINK.value) {
          questionInfo.answer = question.answer === '1'
          addI18nProperty(questionInfo, 'policyLink', 'options[0].content.cn', 'options[0].content.en', question)
        } else if (onboardingQuestionType === ONBOARDING_QUESTION_TYPE.ATTACHMENT.value) {
          Object.defineProperty(questionInfo, 'uploaderTipList', {
            configurable: true,
            enumerable: true,
            get () {
              return [
                vm.$t(`${vm.i18nPrefix}.message.uploadTip.attachment`, { size: 200 })
              ]
            }
          })
        }
        if (questionInfo.type === QUESTION_TYPE.SELECT) {
          questionInfo.answer = parseInt(questionInfo.answer) || null
        }
        questionMap[questionInfo.id] = questionInfo
        const pid = question.pid
        if (pid) {
          const parent = questionMap[pid]
          if (parent) {
            questionInfo._parent = parent
            questionInfo._parentAnswer = parent.answer
          } else {
            let parentChildren = questionPidMap[pid]
            if (!parentChildren) {
              questionPidMap[pid] = parentChildren = []
            }
            parentChildren.push(questionInfo)
          }
          questionInfo._optionList = questionInfo.optionList
          questionInfo._answer = questionInfo.answer
          Object.defineProperty(questionInfo, 'optionList', {
            configurable: true,
            enumerable: true,
            get () {
              const parent = this._parent
              if (!parent.answer) {
                return []
              }
              const parentOption = _.find(parent.optionList, { id: parent.answer })
              return _.filter(this._optionList, { pid: parentOption.id })
            }
          })
          Object.defineProperty(questionInfo, 'answer', {
            configurable: true,
            enumerable: true,
            get () {
              const parent = this._parent
              if (!parent.answer || parent.answer !== this._parentAnswer) {
                return null
              }
              return this._answer
            },
            set (val) {
              const parent = this._parent
              this._parentAnswer = parent.answer
              this._answer = val
            }
          })
        }
        const children = questionPidMap[questionInfo.id]
        if (children) {
          _.forEach(children, child => {
            child._parent = questionInfo
            child._parentAnswer = questionInfo.answer
          })
        }
        return questionInfo
      })
      return questionInfoList
    },
    handleQuestionAnswerChange (questionInfo, answer) {
      if (this.qrImage) {
        questionInfo.errorMessage = null
        return
      }
      if (!this.isQuestionAnswered(answer) && questionInfo.isRequired) {
        questionInfo.errorMessage = `${this.i18nPrefix}.message.requiredQuestionAnswer`
      } else {
        questionInfo.errorMessage = null
      }
      if (this.isTreeMode && !_.isEqual(questionInfo._treeAnswerSnapshot, answer)) {
        const clearedQuestionIds = clearChildTreeAnswers(this.allQuestionList, questionInfo.id)
        this.resetClearedTreeAnswerSnapshots(clearedQuestionIds)
        questionInfo._treeAnswerSnapshot = _.cloneDeep(answer)
        this.syncQuestionTree()
      }
    },
    getQuestionErrorMessage (message) {
      return message && this.$te(message) ? this.$t(message) : message
    },
    isQuestionAnswered (answer) {
      if (_.isDate(answer)) {
        return !Number.isNaN(answer.getTime())
      }
      if (_.isArray(answer)) {
        return answer.length > 0
      }
      if (_.isString(answer)) {
        return answer.trim().length > 0
      }
      if (_.isObject(answer)) {
        return Boolean(answer.videoKey || answer.fileKey || answer.value)
      }
      return answer !== null && answer !== undefined && answer !== ''
    },
    async handleFileUpload (questionInfo, file, callback) {
      let errorMessage = null
      if (questionInfo.uploaderFileTypeList && !questionInfo.uploaderFileTypeList.includes(file.type)) {
        errorMessage = `${this.i18nPrefix}.message.wrongFileType`
      } else if (questionInfo.uploaderFileSize && file.size > questionInfo.uploaderFileSize) {
        errorMessage = `${this.i18nPrefix}.message.wrongFileSize`
      }
      if (errorMessage) {
        questionInfo.errorMessage = errorMessage
        callback()
        return
      }
      questionInfo.isUploading = true
      const fileName = file.name
      const ext = fileName.slice(fileName.indexOf('.'))
      const fileKey = `${getFileUID()}${ext}`
      await uploadFile(file, fileKey, false)
      if (this.questionnaireType === QUESTIONNAIRE_TYPE.OPEN_QUESTION) {
        const originQuestion = questionInfo._question
        if (originQuestion.type === OPEN_QUESTION_TYPE.VIDEO) {
          let resp = await this.handleOpenQuestionUploadedVideo({
            file_key: fileKey,
            question_id: originQuestion.id
          })
          const persistentId = resp.persistent_id
          const maxDuration = parseFloat(originQuestion.duration.slice(0, -1))
          if (maxDuration) {
            let duration = resp.duration
            let isFinished = resp.is_finished
            while (!isFinished) {
              resp = await this.getOpenQuestionUploadedVideoDuration({ persistent_id: persistentId })
              duration = resp.duration
              isFinished = resp.is_finished
              await new Promise((resolve, reject) => {
                setTimeout(resolve, 1000)
              })
            }
            if (duration > maxDuration) {
              this.$message.warning(this.$t(`${this.i18nPrefix}.message.questionVideoIsTooLong`, { duration: originQuestion.duration }))
            }
          }
        }
      }
      callback()
      questionInfo.answer = this.questionnaireType === QUESTIONNAIRE_TYPE.OPEN_QUESTION ? fileKey : `${fileKey} ${fileName}`
      this.handleQuestionAnswerChange(questionInfo, questionInfo.answer)
      questionInfo.isUploading = false
    },
    customValidate () {
      let errorQuestion = _.find(this.stepData, 'errorMessage')
      if (!errorQuestion) {
        errorQuestion = _.find(this.stepData, 'isUploading')
        if (errorQuestion) {
          this.$message.warning(this.$t(`${this.i18nPrefix}.message.uploadTip.waitUploaded`))
        }
      }
      if (errorQuestion) {
        const legacyRef = this.$refs[`q_${errorQuestion.id}`]
        const questionEl = this.questionControlRefs[String(errorQuestion.id)] || (legacyRef && legacyRef[0])
        if (questionEl) {
          questionEl.$el.scrollIntoView({ block: 'center', behavior: 'smooth' })
        }
        return false
      }
      return true
    },
    async validate () {
      _.forEach(this.stepData, questionInfo => {
        this.handleQuestionAnswerChange(questionInfo, questionInfo.answer)
      })
      const isValid = await this.customValidate()
      return Boolean(isValid)
    },
    setStepDataToCandidateInfo () {
      // await allUpload
      _.forEach(this.allQuestionList, questionInfo => {
        const originQuestion = questionInfo._question
        if (this.questionnaireType === QUESTIONNAIRE_TYPE.OPEN_QUESTION) {
          originQuestion.answer_value = questionInfo.answer
          if ([OPEN_QUESTION_TYPE.VIDEO, OPEN_QUESTION_TYPE.ATTACHMENT].includes(originQuestion.type)) {
            originQuestion.answer_file_key = questionInfo.answer
            originQuestion.answer_value = questionInfo.uploaderFileDescription
          } else if (originQuestion.type === OPEN_QUESTION_TYPE.CHECKBOX || originQuestion.type === OPEN_QUESTION_TYPE.MULTIPLE_SELECT) {
            originQuestion.answer_value = _.join(questionInfo.answer)
          } else if (originQuestion.type === OPEN_QUESTION_TYPE.DATE) {
            originQuestion.answer_value = questionInfo.answer ? moment(questionInfo.answer).format('YYYY-MM-DD') : ''
          }
        } else {
          if (originQuestion.type === ONBOARDING_QUESTION_TYPE.LINK.value) {
            originQuestion.answer = questionInfo.answer ? '1' : '0'
          } else {
            originQuestion.answer = questionInfo.answer
          }
        }
      })
    },
    async getStepData () {
      const questionList = _.get(this.candidateInfo, this.infoPropName)
      this.qrImage = null
      if (this.showQrImagePropName) {
        const showQrImage = _.get(this.candidateInfo, this.showQrImagePropName)
        if (showQrImage && this.jobId) {
          const baseUrl = process.env.VUE_APP_STYLE_BASE_URL
          let path = null
          if (showQrImage === 'standard') {
            path = MOBILE_OPEN_QUESTION_PATH
          }
          if (showQrImage === 'invite') {
            path = MOBILE_INVITE_QUESTION_PATH
          }
          this.qrImage = `${baseUrl}qrcode-view/?data=${baseUrl}${path}?jobID=${this.jobId}&account=${this.account.id}`
        }
      }
      this.allQuestionList = this.questionnaireType === QUESTIONNAIRE_TYPE.OPEN_QUESTION ? this.formatOpenQuestion(questionList) : this.formatOnboardingQuestion(questionList)
      this.mode = this.questionnaireType === QUESTIONNAIRE_TYPE.OPEN_QUESTION
        ? detectTreeMode({ questions: this.allQuestionList, meta: this.questionnaireMeta })
        : OPEN_QUESTION_TREE_MODE.FLAT
      this.syncQuestionTree()
      return this.stepData
    }
  }
}
</script>

<style lang="scss" scoped>
.candidate-questionnaire-container {
  &.candidate-answer {
    min-width: 0;
  }

  &.candidate-answer.has-resume-view {
    box-sizing: border-box;
    flex: 0 1 489px;
    width: 489px;
    padding: 0 37.8px 19.99px 60px;
  }

  @media (min-width: 1441px) {
    &.candidate-answer.has-resume-view {
      flex: 0 1 50%;
      width: 50%;
    }
  }

  .tree-summary {
    margin-bottom: 20px;
    padding: 18px;
    border: 1px solid #d8e8f7;
    background:
      radial-gradient(circle at 16px 18px, rgba(27, 128, 218, 0.12), transparent 26px),
      linear-gradient(135deg, #f7fbff 0%, #eef7ff 100%);
    border-radius: 14px;
    box-shadow: 0 10px 30px rgba(31, 89, 145, 0.08);

    .tree-path {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
      margin-top: 14px;
      gap: 12px;
    }

    .path-node {
      position: relative;
      display: flex;
      flex-direction: column;
      gap: 8px;
      min-height: 76px;
      padding: 12px 14px;
      border: 1px solid #d8e8f7;
      border-radius: 12px;
      background: rgba(255, 255, 255, 0.86);
      box-shadow: 0 8px 20px rgba(31, 89, 145, 0.06);

      &::before {
        content: '';
        position: absolute;
        left: -18px;
        top: 38px;
        width: 18px;
        height: 2px;
        background: #7dbdea;
      }
    }

    .path-question-line,
    .path-answer-line {
      display: flex;
      align-items: flex-start;
      gap: 8px;
    }

    .path-depth {
      flex: 0 0 auto;
      min-width: 30px;
      padding: 2px 7px;
      border-radius: 999px;
      background: #e8f4ff;
      color: #0f6fc6;
      font-size: 12px;
      font-weight: 700;
      text-align: center;
    }

    .path-title {
      color: #24384d;
      font-size: 13px;
      font-weight: 600;
      line-height: 18px;
      word-break: break-word;
    }

    .path-answer-label {
      flex: 0 0 auto;
      color: #7a8aa0;
      font-size: 12px;
    }

    .path-answer {
      color: #1776ad;
      font-size: 12px;
      font-weight: 600;
      line-height: 18px;
      word-break: break-word;
    }
  }

  .tree-question-item {
    position: relative;

    &.is-tree-mode {
      margin-left: var(--tree-indent);
      margin-bottom: 18px;
      padding: 18px 20px 16px;
      border: 1px solid #dce9f5;
      border-radius: 16px;
      background: #ffffff;
      box-shadow: 0 12px 28px rgba(31, 89, 145, 0.08);
      transition: border-color 0.2s ease, box-shadow 0.2s ease;

      &::before {
        content: '';
        position: absolute;
        left: -20px;
        top: 0;
        bottom: -18px;
        width: 2px;
        border-radius: 2px;
        background: linear-gradient(180deg, #6db9f7, rgba(109, 185, 247, 0.08));
      }

      &::after {
        content: '';
        position: absolute;
        left: -20px;
        top: 34px;
        width: 18px;
        height: 2px;
        border-radius: 2px;
        background: #6db9f7;
      }

      &:not(.is-tree-child) {
        &::before,
        &::after {
          display: none;
        }
      }

      ::v-deep .question-container {
        margin-bottom: 0;
      }
    }

    .tree-depth-tag {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: 32px;
      padding: 2px 8px;
      border-radius: 999px;
      background: #eef5ff;
      color: #0f6fc6;
      font-size: 12px;
      font-weight: 600;
    }

    .tree-relation-text {
      overflow: hidden;
      color: #4c5d73;
      font-size: 12px;
      line-height: 22px;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  ::v-deep {
    .el-radio__label {
      vertical-align: top;
      word-break: break-word;
      white-space: normal;
      display: inline-block;
      line-height: 16px;
    }

    .el-checkbox__label {
      vertical-align: top;
      word-break: break-word;
      white-space: normal;
    }
  }
}

.has-resume-view {

  .question-container {
    width: 80%;
    margin-left: 60px;
  }

  ::v-deep {

    .question-container {
      margin-bottom: 20px;
      .question-title-container {
        .question-title {
          font-size: 12px;
        }
      }

      .error-message {
        font-size: 12px;
      }

      .question-answer-wrapper {
        .question-answer {
          .file-status {
            .status {
              font-size: 12px;
            }
          }

          .el-button {
            font-size: 12px;
          }
        }
      }
    }

    .el-form-item__label {
      font-size: 12px;
    }
    .label-inner {
      font-size: 12px;
    }

    .el-textarea__inner {
      font-size: 12px;
    }

    .el-input__inner {
      font-size: 12px;
    }

    .el-radio__label {
      font-size: 12px;
    }

    .el-checkbox__label {
      font-size: 12px;
    }
  }

}

.qrImage-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.qrcode-card {
  width: 200px;
  height: 200px;
  margin: 0 auto;

  .qrcode-container {
    position: relative;
    width: 100%;
    height: 100%;

    .qrcode {
      display: block;
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }

  ::v-deep .el-card__body {
    padding: 0;
  }
}

.qrcode-tip {
  font-size: 14px;
  margin-top: 28px;
  text-align: center;
}

</style>
